const REPLACED_ELEMENTS = new Set([
	"img",
	"svg",
	"video",
	"canvas",
	"iframe",
	"input",
	"textarea",
	"select",
	"progress",
]);
const BORDER_WIDTHS = [
	"borderTopWidth",
	"borderRightWidth",
	"borderBottomWidth",
	"borderLeftWidth",
] as const;
const PSEUDO_ELEMENTS = ["::before", "::after"];
const TRANSPARENT_COLOR = /^(transparent|rgba\(.*,\s*0\))$/;

const isSkipped = (node: Node) => {
	const parent = node.parentElement;
	if (parent && REPLACED_ELEMENTS.has(parent.localName)) {
		return true;
	}

	if (!(node instanceof Element)) {
		return false;
	}

	if (node.hasAttribute("data-overlay-scrollbar")) {
		return true;
	}

	const { display, position } = window.getComputedStyle(node);
	return display === "none" || position === "fixed";
};

const hasPseudoContent = (element: Element) =>
	PSEUDO_ELEMENTS.some((pseudo) => {
		const { content } = window.getComputedStyle(element, pseudo);
		return content !== "none" && content !== "normal";
	});

const isPainted = (element: Element) => {
	if (REPLACED_ELEMENTS.has(element.localName)) {
		return true;
	}

	const style = window.getComputedStyle(element);
	return (
		!TRANSPARENT_COLOR.test(style.backgroundColor) ||
		style.backgroundImage !== "none" ||
		style.boxShadow !== "none" ||
		BORDER_WIDTHS.some((width) => Number.parseFloat(style[width]) > 0) ||
		hasPseudoContent(element)
	);
};

const getPaintedRect = (node: Node, range: Range) => {
	if (node instanceof Element) {
		return isPainted(node) ? node.getBoundingClientRect() : null;
	}

	if (!node.textContent?.trim()) {
		return null;
	}

	range.selectNodeContents(node);
	return range.getBoundingClientRect();
};

export const getPaintedContentBottom = (container: HTMLElement): number => {
	const offset =
		container.scrollTop -
		container.getBoundingClientRect().top -
		container.clientTop;
	const range = document.createRange();
	const walker = document.createTreeWalker(
		container,
		NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
		{
			acceptNode: (node) =>
				isSkipped(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
		},
	);

	let bottom = 0;
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		const rect = getPaintedRect(node, range);
		if (rect && rect.width > 0 && rect.height > 0) {
			bottom = Math.max(bottom, rect.bottom + offset);
		}
	}

	return bottom;
};
